import { LiveData } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

/** The left sidebar of the area on screen, as the navigation rail and the shortcut see it. */
export interface AreaSidebar {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const present: AreaSidebar[] = [];

/** Null while the area on screen has no sidebar of its own. */
export const areaSidebar$ = new LiveData<AreaSidebar | null>(null);

const publish = () => areaSidebar$.next(present.at(-1) ?? null);

/** Puts the area's sidebar away or brings it back. False if the area has none. */
export function toggleAreaSidebar(): boolean {
  const sidebar = areaSidebar$.value;
  if (!sidebar) return false;
  sidebar.setOpen(!sidebar.open);
  return true;
}

/** Presents a mounted sidebar as the area's; of several, the one presented last. */
export function useAreaSidebar(
  open: boolean,
  setOpen: (open: boolean) => void,
  enabled = true
) {
  const latest = useRef(setOpen);
  latest.current = setOpen;
  useEffect(() => {
    if (!enabled) return;
    const sidebar: AreaSidebar = {
      open,
      setOpen: next => latest.current(next),
    };
    present.push(sidebar);
    publish();
    return () => {
      present.splice(present.indexOf(sidebar), 1);
      publish();
    };
  }, [enabled, open]);
}
